import { FC, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ChevronNextIcon, ChevronPrevIcon } from '@svgs'
import { getListingNavContext } from 'src/helpers/listingNav'
import { useSelector } from 'react-redux'
import { RootState } from 'src/redux/store'
import { useOptionalSidebar } from 'src/contexts/SidebarContext'

interface IListingNavigation {
  propertyId: string
}

// Flip between neighbouring listings of the feed the user came from:
// desktop — floating side arrows + ArrowLeft/ArrowRight; mobile — a
// horizontal swipe anywhere outside the gallery/map/carousels. Without a
// stored feed context (direct link) nothing renders.
const ListingNavigation: FC<IListingNavigation> = ({ propertyId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const ids = useMemo(() => getListingNavContext(), [])
  // The left arrow sat on top of the sidebar, and the position counter in the
  // very spot of the compare bar.
  const sidebar = useOptionalSidebar()
  const compareBarShown = useSelector(
    (state: RootState) => state.propertySearch.compareIds.length >= 2,
  )
  const leftArrowClass = !sidebar
    ? 'left-3'
    : sidebar.collapsed
      ? 'left-[5.75rem]'
      : 'left-[23.75rem]'

  const index = ids ? ids.indexOf(propertyId) : -1
  const prevId = ids && index > 0 ? ids[index - 1] : null
  const nextId = ids && index >= 0 && index < ids.length - 1 ? ids[index + 1] : null

  const goTo = useCallback(
    (target: string | null) => {
      if (!target) return
      navigate(`/announcement/${target}`)
      // The route param changes without a remount — reset the scroll so the
      // next listing starts from its gallery, not mid-page.
      window.scrollTo({ top: 0 })
    },
    [navigate],
  )

  useEffect(() => {
    if (index < 0) return
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      const el = document.activeElement as HTMLElement | null
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) {
        return
      }
      if (event.key === 'ArrowLeft') goTo(prevId)
      if (event.key === 'ArrowRight') goTo(nextId)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, prevId, nextId, goTo])

  useEffect(() => {
    if (index < 0) return
    // Zones with their own horizontal gestures must win over the page swipe.
    const IGNORE =
      '.swiper, .mapboxgl-map, .gm-style, [data-noswipe], .MuiModal-root, input, textarea'
    let startX = 0
    let startY = 0
    let eligible = false
    const onStart = (event: TouchEvent) => {
      const target = event.target as HTMLElement | null
      eligible = !(target && target.closest(IGNORE))
      startX = event.touches[0].clientX
      startY = event.touches[0].clientY
    }
    const onEnd = (event: TouchEvent) => {
      if (!eligible) return
      const dx = event.changedTouches[0].clientX - startX
      const dy = event.changedTouches[0].clientY - startY
      if (Math.abs(dx) < 70 || Math.abs(dy) > 60) return
      goTo(dx < 0 ? nextId : prevId)
    }
    document.addEventListener('touchstart', onStart, { passive: true })
    document.addEventListener('touchend', onEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onStart)
      document.removeEventListener('touchend', onEnd)
    }
  }, [index, prevId, nextId, goTo])

  if (index < 0 || (!prevId && !nextId)) return null

  const arrowClass =
    'hidden md:flex fixed top-1/2 -translate-y-1/2 z-40 w-11 h-11 items-center justify-center rounded-full bg-surface shadow-[0rem_.25rem_1.5rem_rgba(0,0,0,0.18)] fill-ink hover:bg-brand-strong hover:fill-white transition-colors duration-200'

  return (
    <>
      {prevId && (
        <button
          type="button"
          aria-label={t('listing_nav.prev')}
          title={t('listing_nav.prev')}
          onClick={() => goTo(prevId)}
          className={`${arrowClass} ${leftArrowClass}`}
        >
          <span className="icon-button w-3 h-3">
            <ChevronPrevIcon />
          </span>
        </button>
      )}
      {nextId && (
        <button
          type="button"
          aria-label={t('listing_nav.next')}
          title={t('listing_nav.next')}
          onClick={() => goTo(nextId)}
          className={`${arrowClass} right-3`}
        >
          <span className="icon-button w-3 h-3">
            <ChevronNextIcon />
          </span>
        </button>
      )}
      {/* Position within the feed — desktop only, floats above the footer */}
      <div
        className={`hidden md:flex fixed ${compareBarShown ? 'bottom-20' : 'bottom-6'} left-1/2 -translate-x-1/2 z-40 px-3 py-1 rounded-full bg-inverse bg-opacity-70 text-white text-xs pointer-events-none`}
      >
        {index + 1} / {ids!.length}
      </div>
    </>
  )
}

export default ListingNavigation
